/** Copyright (c) 2020 T. Rowe Price. All rights reserved. */
import './ChallengeDeposit.scss';

import React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { object } from 'prop-types';
import TRP from 'components/shared/TRP/TRP';
import SaveLoadingButtonWithPermissions from 'components/shared/LoadingButton/SaveLoadingButtonWithPermissions';
import FormattedInput from 'components/shared/FormattedInput/FormattedInput';
import { getBankProfile, captureBankingBusinessEvent } from 'services/Rest';
import {
  getCustomer,
  getPrimaryEmailAddress
} from 'state/customer/CustomerSelectors';
import {
  getView,
  hasAlaska529Only,
  hasMaryland529Only,
  hasNational529Only
} from 'state/view/ViewSelectors';
import {
  challengeDeposit,
  buildBusinessEvent,
  BANK_EVENT_TYPES
} from 'services/LinkedBankAccounts';
import Content, { contentConverter } from 'services/Content';
import { getBanksInErrorDepositVerification } from 'state/linkedBankAccounts/LinkedBankAccountsSelectors';
import Store from 'state/Store';
import { clearBanksInErrorChallengeDeposit } from 'state/linkedBankAccounts/LinkedBankAccountsActions';
import { getENV, TRP_ENVIRONMENTS } from 'services/globals';

class ChallengeDeposit extends React.Component {
  state = {
    deposits: ['', '']
  };

  componentWillUnmount() {
    Store.dispatch(clearBanksInErrorChallengeDeposit());
  }

  onDepositChange = (depositId) => (newValue) => {
    this.setState((state) => {
      const { deposits } = state;
      const newDeposits = deposits.map((value, id) =>
        id === depositId ? newValue : value
      );
      return { deposits: newDeposits };
    });
  };

  autoFill = () => {
    this.setState({
      deposits: ['$0.09', '$0.02']
    });
  };

  areDepositsInvalid() {
    const { deposits } = this.state;
    return deposits.some((value) => value.length < 5);
  }

  onClickVerify = () => {
    const { bankAccount } = this.props;
    const { deposits } = this.state;
    const { customerEmail, customer, view } = this.props;

    let associationIds = [];
    const { bankAssociations } = bankAccount;
    if (bankAssociations) {
      bankAssociations.forEach((assoc) => {
        associationIds.push(assoc.accountDescriptor.accountId);
      });
    }

    const event = buildBusinessEvent(
      BANK_EVENT_TYPES.COMPLETE,
      customerEmail,
      customer,
      view,
      bankAccount,
      associationIds
    );

    bankAccount.bankRestrictions = bankAccount.bankRestrictions.map(
      (r) => r && r.restriction && r.restriction
    );

    challengeDeposit(bankAccount, deposits, () => {
      getBankProfile();
      captureBankingBusinessEvent(event);
    });
  };

  render() {
    const { deposits } = this.state;
    const {
      bankAccount: { bankId, bankName },
      banksInError = [],
      hasAlaska529Only,
      hasMaryland529Only,
      hasNational529Only
    } = this.props;
    const isBankInError = banksInError.includes(bankId);

    const isNonProd = getENV() !== TRP_ENVIRONMENTS.PRODUCTION;

    const inputClassName = classnames('challenge-deposit-formatted-input', {
      'challenge-deposit-error': isBankInError
    });

    return (
      <div className="challenge-deposit-container">
        <div className="challenge-deposit-content">
          <div className="challenge-deposit-title">Verify Your Bank</div>
          <div className="challenge-deposit-copy">
            Check your {bankName} account activity and indicate the two deposits
            of less than $1.00 each from <TRP />.
          </div>
          <div className="challenge-deposit-controls">
            {deposits.map((_, idx) => (
              <div className="challenge-deposit-input" key={idx}>
                <div className="challenge-deposit-id">Deposit #{idx + 1}</div>
                <FormattedInput
                  className={inputClassName}
                  format={'challenge-deposit'}
                  placeholder={'$0.00'}
                  value={deposits[idx]}
                  onChange={this.onDepositChange(idx)}
                />
              </div>
            ))}

            {isNonProd && (
              <div className={'challenge-deposit--button-container'}>
                <div
                  className={'action-button-link secondary-button'}
                  onClick={this.autoFill}>
                  NonProd: Auto Fill Deposits
                </div>
              </div>
            )}

            <div className="challenge-deposit--button-container">
              <SaveLoadingButtonWithPermissions
                className="challenge-deposit-button"
                id="verify-challenge-deposit-button"
                disabled={this.areDepositsInvalid()}
                onClick={this.onClickVerify}
                requestStatusKey="challengeDepositVerification"
                shouldDisable>
                Verify
              </SaveLoadingButtonWithPermissions>
            </div>
          </div>
          {isBankInError &&
            !hasAlaska529Only &&
            !hasNational529Only &&
            !hasMaryland529Only && (
              <span
                className="challenge-deposit-error-text"
                dangerouslySetInnerHTML={{
                  __html: contentConverter.makeHtml(
                    Content.challengeDepositFailed
                  )
                }}
              />
            )}
          {isBankInError && hasAlaska529Only && (
            <span
              className="challenge-deposit-error-text"
              dangerouslySetInnerHTML={{
                __html: contentConverter.makeHtml(
                  Content.challengeDepositFailedAKOnly
                )
              }}
            />
          )}
          {isBankInError && hasMaryland529Only && (
            <span
              className="challenge-deposit-error-text"
              dangerouslySetInnerHTML={{
                __html: contentConverter.makeHtml(
                  Content.challengeDepositFailedMDOnly
                )
              }}
            />
          )}
          {isBankInError && hasNational529Only && (
            <span
              className="challenge-deposit-error-text"
              dangerouslySetInnerHTML={{
                __html: contentConverter.makeHtml(
                  Content.challengeDepositFailedNATOnly
                )
              }}
            />
          )}
          <div className="challenge-deposit--button-container-responsive">
            <SaveLoadingButtonWithPermissions
              className="challenge-deposit-button"
              id="verify-challenge-deposit-button"
              disabled={this.areDepositsInvalid()}
              onClick={this.onClickVerify}
              requestStatusKey="challengeDepositVerification"
              shouldDisable>
              Verify
            </SaveLoadingButtonWithPermissions>
          </div>
        </div>
      </div>
    );
  }
}

ChallengeDeposit.propTypes = {
  bankAccount: object.isRequired
};

const mapStateToProps = (state) => ({
  banksInError: getBanksInErrorDepositVerification(state),
  customerEmail: getPrimaryEmailAddress(state).address || '',
  customer: getCustomer(state),
  hasAlaska529Only: hasAlaska529Only(state),
  hasMaryland529Only: hasMaryland529Only(state),
  hasNational529Only: hasNational529Only(state),
  view: getView(state)
});

export default connect(mapStateToProps)(ChallengeDeposit);
